import type { AlertAcknowledgement, Alert as AlertEntry } from '@/domain/alerts';
import type { ActiveBioscope } from '@/domain/bioscopes';

export type BioscopeRowAlert = AlertEntry & { acknowledged: boolean };

/** A bioscope with its open alerts, each marked with whether someone silenced it. */
export type BioscopeRow = ActiveBioscope & { alerts: BioscopeRowAlert[] };

/**
 * An acknowledgement without an alert id silences every alert with that code at the location;
 * one with an id silences only that alert.
 */
export function isAlertAcknowledged(
    acknowledgements: AlertAcknowledgement[],
    alert: AlertEntry,
    /** Undefined for a bioscope on land, which no silence covers. */
    locationId: number | undefined
): boolean {
    return acknowledgements.some(
        (ack) =>
            ack.alertCode === alert.alertCode &&
            ack.locationId === locationId &&
            ack.acknowledged &&
            (ack.alertId === null || ack.alertId === alert.id)
    );
}

interface RowBioscope {
    id: number;
    lastLocation?: { id: number };
}

export function toBioscopeRows<B extends RowBioscope>(
    bioscopes: B[],
    alerts: AlertEntry[],
    acknowledgements: AlertAcknowledgement[]
): Array<B & { alerts: BioscopeRowAlert[] }> {
    return bioscopes.map((bioscope) => {
        return {
            ...bioscope,
            alerts: alerts
                .filter((alert) => alert.bioscopeId === bioscope.id)
                .map((alert) => ({
                    ...alert,
                    acknowledged: isAlertAcknowledged(
                        acknowledgements,
                        alert,
                        bioscope.lastLocation?.id
                    ),
                })),
        };
    });
}
